import { ApplicationSource } from 'src/services/applications/applications.types';

/**
 * Where an application came from, as one label every screen agrees on.
 *
 * The backend records it in three fields. `apply_source` is the org's own tracked channel,
 * `source_detail` is a job board or an untracked link value, and `source` is how the application
 * arrived. The most specific one that is set wins. Applications from the import tool show the board
 * the email was forwarded from, marked so it can't be mistaken for a direct apply.
 */

type Translate = (key: string, options?: { defaultValue?: string }) => string;

export type ApplicationSourceFields = {
  source?: ApplicationSource | string | null;
  source_detail?: string | null;
  // A plain string on the lean list, `{ id, source }` on everything built by buildForTenant.
  apply_source?: string | { source?: string | null } | null;
};

export type ApplicationSourceDisplay = {
  // null when nothing is known about where the application came from.
  label: string | null;
  viaImport: boolean;
};

// Boards with a casing a title-case pass would get wrong (LinkedIn).
const KNOWN_BOARDS = ['seek', 'linkedin', 'indeed'];

// What the import tool records when it cannot tell which board an email came from.
const UNDETECTED_BOARD = 'other';

// Channel names and link values are stored lowercase ("seek", "facebook_ads").
function formatSourceName(value: string, t: Translate) {
  const key = value.trim().toLowerCase();

  if (KNOWN_BOARDS.includes(key)) {
    return t(`enums.application_source_board.${key}`);
  }

  return key
    .replace(/_/g, ' ')
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function getApplicationSource(application: ApplicationSourceFields, t: Translate): ApplicationSourceDisplay {
  const channel = typeof application.apply_source === 'string'
    ? application.apply_source
    : application.apply_source?.source;

  if (channel?.trim()) {
    return { label: formatSourceName(channel, t), viaImport: false };
  }

  const detail = application.source_detail?.trim();

  if (application.source === ApplicationSource.EMAIL_INBOUND) {
    const boardKnown = !!detail && detail.toLowerCase() !== UNDETECTED_BOARD;
    return {
      label: boardKnown ? formatSourceName(detail, t) : t('applications.source.undetected_board'),
      viaImport: true,
    };
  }

  if (detail) {
    return { label: formatSourceName(detail, t), viaImport: false };
  }

  if (application.source) {
    return {
      label: t(`enums.application_source.${application.source}`, { defaultValue: application.source }),
      viaImport: false,
    };
  }

  return { label: null, viaImport: false };
}
